// src/components/ListView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Edit, Trophy } from 'lucide-react';
import { sortByRating, getRankIcon } from '../utils/eloUtils';
import {getAllItems ,validateAccess} from '../utils/api'; // updated import
import { generateFingerprint } from '../utils/fingerprint';
import { Eye, EyeOff } from 'lucide-react';
import LoadingSpinner from '../utils/loadingSpinner'
import Footer from "./Footer"


// Demo data for offline demo
const DEMO_DATA = {
  DEMO123: {
    id: 'DEMO123',
    publicId: 'DEMO123',
    title: 'Favorite Programming Languages',
    description: 'Vote for your favorite programming language',
    items: [
      { id: '1', name: 'JavaScript', elo: 1250, wins: 3, losses: 1, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRfQ4MH3vLA6l72ULn3Up_6undPBcXoERMFcA&s' },
      { id: '2', name: 'TypeScript', elo: 1220, wins: 2, losses: 1, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Typescript.svg/1200px-Typescript.svg.png' },
      { id: '3', name: 'Python', elo: 1200, wins: 2, losses: 2, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Python-logo-notext.svg/1200px-Python-logo-notext.svg.png' },
      { id: '4', name: 'React', elo: 1180, wins: 1, losses: 3, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcR5U16C8yXgBpl7-Bc7Itjx3_LRl425zINA&s' }
    ],
    totalComparisons: 6,
    creatorId: 'demo-creator',
    adminCode: 'ADMIN1'
  }
};

const ListView = ({ creatorId }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminCode, setAdminCode] = useState('');
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  
    useEffect(() => {
    const loadList = async () => {
      try {
        setLoading(true);

        // Demo mode fallback
        // if (DEMO_DATA[listId]) {
        //   setList(DEMO_DATA[listId]);
        //   setIsCreator(DEMO_DATA[listId].creatorId === creatorId);
        //   setLoading(false);
        //   return;
        // }

        const fingerprint = await generateFingerprint();
        const result = await getAllItems(listId, fingerprint);
        const itemsArray = Object.entries(result.items).map(([id, data]) => ({
          id,
          ...data
        }));

        setList({
          id: listId,
          publicId: listId,
          title: result.meta.name || 'Untitled List',
          description: result.meta.description || '',
          items: itemsArray,
          totalComparisons: result.meta.total_comparisons || 0,
          //creatorId: result.meta.creatorId || null,
          adminCode: result.permissions?.Password || null
        });

        setIsCreator(result.permissions?.owner === true);
      } catch (err) {
        console.error('Error loading list:', err);
        setError('List not found');
      } finally {
        setLoading(false);
        window.scrollTo(0, 0);
      }
    };

    loadList();
    }, [listId, creatorId]);

  const copyAdminCode = async () => {
    if (list?.adminCode) {
      try {
        await navigator.clipboard.writeText(list.adminCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = list.adminCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleAdminAccess = async () => {
    // if (listId === "DEMO123" && adminCode === "ADMIN1"){
    //     document.cookie = `${listId}=${adminCode}; path=/; secure; SameSite=Strict`;
    //     navigate(`/admin/${listId}`);
    //     return
    // }
    const isValidAdminCode = /^[a-zA-Z0-9]{8}$/.test(adminCode);
    if (isValidAdminCode){
      const authorised = await validateAccess(listId, adminCode);
      if (adminCode === list?.adminCode || authorised.success) {
        document.cookie = `${listId}=${adminCode}; path=/; secure; SameSite=Strict`;
        navigate(`/admin/${listId}`);
      } else {
        alert('Invalid admin code');
        setAdminCode('');}
    }
    else{
      alert('Invalid admin code');
      setAdminCode('');}
  };

  const handleItemClick = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  if (loading) return <LoadingSpinner />
  if (error) return <div className="container"><div className="error">Error: {error}</div></div>;
  if (!list) return <div className="container"><div className="error">List not found</div></div>;

  const sortedItems = sortByRating(list.items);

  return (
    <div className="container">



      <button onClick={() => navigate('/')} className="back-btn">
        <ArrowLeft size={20} />
        Back to Home
      </button>

      <button onClick={() => navigate('/create')} className="create-btn">
        Create New
      </button>

      <div className="list-header">
        <h1>{list.title}</h1>
        <h2>{list.description}</h2>
        <div className="list-info">
          <span>List ID: {list.publicId || listId}</span>
          <button onClick={() => navigator.clipboard.writeText(list.publicId || listId)} className="copy-btn">
            <Copy size={16} />
          </button>
          {isCreator && (
            <>
              <span>Admin Code: ********</span>
              <button onClick={copyAdminCode} className="copy-btn">
                <Copy size={16} />
                {copied && <span className="copied-text">Copied!</span>}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="action-buttons">
        <button onClick={() => navigate(`/compare/${listId}`)} className="btn btn-compare">
          Start Comparing
        </button>
        <button onClick={() => setShowAdminInput(!showAdminInput)} className="btn btn-secondary">
          <Edit size={16} />
          Admin Access
        </button>
      </div>

      {showAdminInput && (
        <div className="admin-input">
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter admin code"
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value)}
              className="input password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle-btn"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <button onClick={handleAdminAccess} className="btn btn-primary">
            Access
          </button>
        </div>
      )}

      <div className="stats">
        <p>Total Comparisons: {list.totalComparisons || 0}</p>
      </div>

      <div className="leaderboard">
        <h2>
          <Trophy size={24} />
          Leaderboard
        </h2>
        {sortedItems.map((item, index) => (
          <div 
            key={item.id} 
            className={`leaderboard-item ${expandedItem === item.id ? 'expanded' : ''}`}
            onClick={() => handleItemClick(item.id)}
          >
            <div className="item-content">
              <div className="rank">
                <span className="rank-icon">{getRankIcon(index + 1)}</span>
                <span className="rank-number">#{index + 1}</span>
              </div>
              <div className="item-info">
                {expandedItem === item.id ? (
                  // Expanded layout with better image handling
                  <div className="expanded-layout">
                    {item.imageUrl && (
                      <div className="expanded-image-container">
                        <img src={item.imageUrl} alt={item.name} className="expanded-item-image" />
                      </div>
                    )}
                    <div className="expanded-content">
                      <h3>{item.name}</h3>
                      <div className="item-stats-grid">
                        <div className="stat-item">
                          <div className="stat-label">Rating</div>
                          <div className="stat-value">{item.elo}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Wins</div>
                          <div className="stat-value">{item.wins}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Losses</div>
                          <div className="stat-value">{item.losses}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Win Rate</div>
                          <div className="stat-value">
                            {item.wins + item.losses > 0 
                              ? `${Math.round((item.wins / (item.wins + item.losses)) * 100)}%`
                              : 'N/A'
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Collapsed layout
                  <div className="collapsed-layout">
                    {item.imageUrl && (
                      <img src={item.imageUrl} alt={item.name} className="item-image" />
                    )}
                    <div>
                      <h3>{item.name}</h3>
                      <p>Rating: {item.elo} | Wins: {item.wins} | Losses: {item.losses}</p>
                      <p className="click-hint">Click to expand details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Footer />
      

      
    </div>
  );
};

export default ListView;